const normalizeKey = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/https?:\/\//g, "")
    .replace(/^www\./, "")
    .replace(/[^a-z0-9]/g, "");

const REPORTS = [
  {
    aliases: ["aws", "amazonwebservices", "amazonaws", "amazon"],
    fileName: "AWS SOC 2 Type II 2025.pdf",
    auditor: "Ernst & Young LLP",
    period: "Oct 1, 2024 – Sep 30, 2025",
    opinion: "Unqualified",
    trustCenterUrl: "https://aws.amazon.com/trust-center/",
  },
  {
    aliases: ["okta"],
    fileName: "Okta SOC 2 Type II.pdf",
    auditor: "PricewaterhouseCoopers LLP",
    period: "Feb 1, 2025 – Jan 31, 2026",
    opinion: "Unqualified",
    trustCenterUrl: "https://trust.okta.com",
  },
  {
    aliases: ["slack"],
    fileName: "Slack SOC 2 Type II.pdf",
    auditor: "Deloitte & Touche LLP",
    period: "Sep 1, 2024 – Aug 31, 2025",
    opinion: "Unqualified",
    status: "Expired",
    trustCenterUrl: "https://slack.com/trust",
  },
  {
    aliases: ["datadog"],
    fileName: "Datadog SOC 2 Type II.pdf",
    auditor: "Ernst & Young LLP",
    period: "Nov 1, 2024 – Oct 31, 2025",
    opinion: "Unqualified",
    trustCenterUrl: "https://trust.datadoghq.com/",
  },
  {
    aliases: ["stripe"],
    fileName: "Stripe SOC 2 Type II.pdf",
    auditor: "Ernst & Young LLP",
    period: "Jan 1, 2025 – Dec 31, 2025",
    opinion: "Unqualified",
    trustCenterUrl: "https://docs.stripe.com/security",
  },
  {
    aliases: ["notion"],
    fileName: "Notion SOC 2 Type II.pdf",
    auditor: "Schellman & Company LLC",
    period: "Mar 1, 2025 – Feb 28, 2026",
    opinion: "Unqualified",
    trustCenterUrl: "https://trust.notion.com/",
  },
  {
    aliases: ["zoom"],
    fileName: "Zoom SOC 2 Type II.pdf",
    auditor: "Deloitte & Touche LLP",
    period: "Feb 1, 2024 – Jan 31, 2025",
    opinion: "Unqualified",
    trustCenterUrl: "https://www.zoom.com/en/trust/",
  },
  {
    aliases: ["github"],
    fileName: "GitHub SOC 2 Type II.pdf",
    auditor: "Ernst & Young LLP",
    period: "Apr 1, 2025 – Mar 31, 2026",
    opinion: "Unqualified",
    trustCenterUrl: "https://github.com/security",
  },
  {
    aliases: ["figma"],
    fileName: "Figma SOC 2 Type II.pdf",
    auditor: "A-LIGN",
    period: "Jan 1, 2025 – Dec 31, 2025",
    opinion: "Unqualified",
    trustCenterUrl: "https://www.figma.com/security",
  },
  {
    aliases: ["hubspot"],
    fileName: "HubSpot SOC 2 Type II.pdf",
    auditor: "Schellman & Company LLC",
    period: "May 1, 2025 – Apr 30, 2026",
    opinion: "Unqualified",
    trustCenterUrl: "https://trust.hubspot.com/",
  },
  {
    aliases: ["openai"],
    fileName: "OpenAI SOC 2 Type II.pdf",
    auditor: "A-LIGN",
    period: "Jun 1, 2025 – May 31, 2026",
    opinion: "Unqualified",
    trustCenterUrl: "https://trust.openai.com/",
  },
];

const isSoc2 = (file) =>
  String(file?.type || "").toLowerCase().includes("soc 2") ||
  String(file?.name || "").toLowerCase().includes("soc 2");

export const getKnownVendorReport = (vendor = {}) => {
  const haystack = `${normalizeKey(vendor.name)} ${normalizeKey(vendor.website)} ${normalizeKey(
    vendor.trustCenterUrl
  )}`;
  if (!haystack.trim()) return null;

  const match = REPORTS.find((report) =>
    report.aliases.some((alias) => haystack.includes(alias))
  );
  if (!match) return null;

  return {
    name: match.fileName,
    type: "SOC 2 Type II",
    status: match.status || "Valid",
    source: "Trust Center",
    auditor: match.auditor,
    period: match.period,
    opinion: match.opinion,
    trustCenterUrl: vendor.trustCenterUrl || match.trustCenterUrl,
    uploadedAt: new Date().toISOString(),
    expiresAt: null,
  };
};

export const isKnownVendor = (vendor) => !!getKnownVendorReport(vendor);

export const findSoc2File = (files = []) => files.find(isSoc2) || null;

export const resolveSoc2Files = (vendor = {}) => {
  const fromEvidence = (vendor.evidence || []).filter(isSoc2);
  if (fromEvidence.length) return fromEvidence.map((file) => ({ ...file }));

  const fromReviews = (vendor.reviews || [])
    .flatMap((review) => review.files || [])
    .filter(isSoc2);
  if (fromReviews.length) return [{ ...fromReviews[0] }];

  const known = getKnownVendorReport(vendor);
  return known ? [known] : [];
};

export const enrichSoc2File = (file, vendor) => {
  const known = getKnownVendorReport(vendor);
  if (!file && !known) return null;
  return {
    ...(known || {}),
    ...(file || {}),
    auditor: file?.auditor || known?.auditor,
    period: file?.period || known?.period,
    opinion: file?.opinion || known?.opinion || "Unqualified",
    trustCenterUrl: vendor.trustCenterUrl || file?.trustCenterUrl || known?.trustCenterUrl,
  };
};
